/**
 * AdminLogin — sign-in entry to the admin console.
 * Light-first, dark-aware. Uses the new design system tokens.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Book } from "lucide-react";
import { ThemeToggle } from "@/components/ui/theme-toggle";

const AdminLogin = () => {
    const { user, login, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (user && !loading) navigate("/admin/dashboard", { replace: true });
    }, [user, loading, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-5 h-5 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background relative flex flex-col items-center justify-center px-4" data-testid="admin-login-page">
            {/* Background pattern */}
            <div className="fixed inset-0 bg-grid-pattern-light dark:bg-grid-pattern pointer-events-none" aria-hidden="true" />
            <div className="fixed inset-0 bg-hero-glow-light dark:bg-hero-glow-dark pointer-events-none" aria-hidden="true" />

            {/* Theme toggle floating top-right */}
            <div className="absolute top-4 right-4 z-10">
                <ThemeToggle compact />
            </div>

            <div className="relative z-10 flex flex-col items-center fade-up">
                <div className="flex items-center gap-3 mb-10">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-950 dark:bg-white">
                        <Book className="h-6 w-6 text-white dark:text-zinc-950" />
                    </div>
                    <div>
                        <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-950 dark:text-white">Emergent Docs</h1>
                        <p className="eyebrow text-zinc-500">Admin Console</p>
                    </div>
                </div>

                <div className="w-full max-w-sm rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-8 shadow-sm">
                    <h2 className="font-heading text-xl font-black tracking-tight text-zinc-950 dark:text-white text-center mb-1">
                        Welcome back
                    </h2>
                    <p className="text-zinc-600 dark:text-zinc-300 text-sm text-center mb-8">
                        Sign in to manage your documentation
                    </p>

                    <button
                        type="button"
                        onClick={login}
                        className="btn-press w-full flex items-center justify-center gap-3 h-11 bg-zinc-950 dark:bg-white text-white dark:text-zinc-950 rounded-md font-bold text-sm hover:opacity-90"
                        data-testid="google-login-btn"
                    >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Continue with Google
                    </button>

                    <div className="mt-6 text-center">
                        <a href="/" className="text-xs text-zinc-600 dark:text-zinc-300 hover:text-brand transition-colors">
                            ← Back to documentation
                        </a>
                    </div>
                </div>

                <p className="mt-10 eyebrow text-zinc-400">Powered by Emergent</p>
            </div>
        </div>
    );
};

export default AdminLogin;
